import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "src/utils/api";
import Helper from "../utils/helper";
import Constants from "@/utils/constants";

export const useBladesStore = defineStore("blades_store", {
  persist: true,
  state: () => ({
    bladeEventsList: []
  }),
  actions: {
    async getBladesShops(config, offlineFirst = false) {
      const response = await Api.Blades.getBladesShops(config, offlineFirst);
      return response;
    },
    async getBladesUnits(config, offlineFirst = false) {
      const response = await Api.Blades.getBladesUnits(config, offlineFirst);
      return response;
    },
    async getBladesUnitsWithChangeInfo(shopIds) {
      const response = await Api.Blades.getBladesUnitsWithChangeInfo(shopIds);
      return response;
    },
    async revertBladeEvent(eventData) {
      const response = await Api.Blades.revertBladeEvent(eventData);
      return response;
    },
    async getBladeEventDetails(eventId) {
      const response = await Api.Blades.getBladeEventDetails(eventId);
      return response;
    },
    async updateBladesUnit(data, offlineFirst = false) {
      const response = await Api.Blades.updateBladesUnit(data, offlineFirst);
      return response;
    },
    async insertBladeEvent(data, offlineFirst = false) {
      const response = await Api.Blades.insertBladeEvent(data, offlineFirst);
      return response;
    },
    async getBladesInventoryView(config, offlineFirst = false) {
      const response = await Api.Blades.getBladesInventoryView(
        config,
        offlineFirst
      );
      return response;
    },
    async getBladesInventoryReconciliationDiscrepancies() {
      const response = await Api.Blades.getBladesInventoryReconciliationDiscrepancies();
      return response;
    },
    async getBladesInventory(config, offlineFirst = false) {
      const response = await Api.Blades.getBladesInventory(
        config,
        offlineFirst
      );
      return response;
    },
    async getCommentsForUnit(unitId, offlineFirst = false) {
      try {
        const events = await Api.Blades.getBladeEventsForUnit(
          {
            sortField: "created_at",
            sortOrder: -1,
            queryParams: [
              {
                field: "unit_id",
                value: unitId,
                opType: Constants.enums.COMPARISON_TYPE.EQ
              }
            ]
          },
          offlineFirst
        );
        if (!events || !events.data) {
          return [];
        }

        // Filter events that have comments and return only the comment data
        return events.data.map((event) => ({
          id: event.id,
          event_type: event.event_type,
          changed_on: event.changed_on,
          comments: event.comments || "No comment",
          created_at: event.created_at,
          created_by: event.created_by,
          parent_id: event.parent_id
        }));
      } catch (error) {
        console.error("Error getting comments for unit:", error);
        return [];
      }
    },
    async getAllEventsForUnit(
      unitId,
      eventType = null,
      offlineFirst = false
    ) {
      try {
        const events = await Api.Blades.getBladeEventsForUnit(
          {
            sortField: "created_at",
            sortOrder: -1,
            queryParams: eventType
              ? [
                  {
                    field: "unit_id",
                    value: unitId,
                    opType: Constants.enums.COMPARISON_TYPE.EQ
                  },
                  {
                    field: "event_type",
                    value: eventType,
                    opType: Constants.enums.COMPARISON_TYPE.EQ
                  }
                ]
              : []
          },
          offlineFirst
        );
        if (!events || !events.data || events.data.length === 0) {
          return null;
        }

        // Return the most recent event (first in the ordered list)
        return events.data;
      } catch (error) {
        console.error("Error getting all events for unit:", error);
        return null;
      }
    },
    async getMostRecentEventForUnit(
      unitId,
      eventType = null,
      offlineFirst = false
    ) {
      try {
        const events = await Api.Blades.getBladeEventsForUnit(
          {
            sortField: "created_at",
            sortOrder: -1,
            queryParams: eventType
              ? [
                  {
                    field: "unit_id",
                    value: unitId,
                    opType: Constants.enums.COMPARISON_TYPE.EQ
                  },
                  {
                    field: "event_type",
                    value: eventType,
                    opType: Constants.enums.COMPARISON_TYPE.EQ
                  }
                ]
              : []
          },
          offlineFirst
        );
        if (!events || !events.data || events.data.length === 0) {
          return null;
        }

        // Return the most recent event (first in the ordered list)
        return events.data.length > 0 ? events.data[0] : null;
      } catch (error) {
        console.error("Error getting most recent event for unit:", error);
        return null;
      }
    },
    async addBladesAdjustmentDetails(data, offlineFirst = false) {
      const response = await Api.Blades.addBladesAdjustmentDetails(
        data,
        offlineFirst
      );
      return response;
    }
  },
  $reset() {}
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBladesStore, import.meta.hot));
}
